import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  FileText,
  Download,
  Plus,
  Eye,
  CheckCircle,
  AlertTriangle,
  Calendar,
  DollarSign,
  FileSpreadsheet,
  Trash2,
  Loader2,
  AlertCircle,
  Banknote,
} from 'lucide-react';
import { HelpTooltip } from '../../components/ui/help-tooltip';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Column help text definitions
const COLUMN_HELP = {
  totalSales: "Grand total of all paid invoices (subtotal + shipping + tax). This is the full invoice amount collected from customers.",
  shipping: "Total shipping charges collected across all paid invoices. Shipping is tracked separately from product sales.",
  taxable: "CA taxable sales - the tax base for CDTFA. Sum of taxable amounts (subtotal - discount) for CA PICKUP + CA SHIP orders. Tax owed = Taxable × Rate.",
  nonTaxable: "Sum of taxable amounts for invoices NOT subject to CA sales tax. Includes: Non-CA shipments + Tax-exempt customers. Does NOT include shipping or tax collected.",
  taxCollected: "Total sales tax actually charged on invoices and collected from customers.",
  taxOwed: "Tax liability to CDTFA. Calculated as: CA taxable sales × default rate. Only CA sales (PICKUP + SHIP to CA) generate tax owed.",
  overCollected: "Tax Collected minus Tax Owed. Positive = over-collected (liability), Negative = under-collected.",
  caTaxable: "Taxable amount for CA sales (PICKUP + SHIP to CA addresses). This is the tax base for CDTFA.",
  nonCaTaxable: "Taxable amount for non-CA shipments. These sales are not subject to CA sales tax but may have had tax collected (over-collection).",
  exceptions: "Data quality issues: missing ship-to state, missing tax info, or non-CA orders with tax collected.",
};

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount || 0);
};

// Format percentage
const formatPercent = (rate) => {
  return `${((rate || 0) * 100).toFixed(2)}%`;
};

// Status badge colors
const getStatusBadge = (status) => {
  switch (status) {
    case 'draft':
      return <Badge variant="secondary">Draft</Badge>;
    case 'final':
      return <Badge variant="default">Final</Badge>;
    case 'filed':
      return <Badge className="bg-green-600">Filed</Badge>;
    default:
      return <Badge variant="outline">{status || 'Unknown'}</Badge>;
  }
};

// Generate period options (last 24 months)
const generatePeriodOptions = () => {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 24; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const label = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const display = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    options.push({ value: label, label: display });
  }
  return options;
};

export default function AdminSalesTaxReports({ onViewReport }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [defaultTaxRate, setDefaultTaxRate] = useState(null);

  const periodOptions = generatePeriodOptions();
  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];

  // Fetch reports
  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      let url = `${API_BASE}/api/tax/ca/reports?limit=100`;
      if (statusFilter && statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      if (yearFilter && yearFilter !== 'all') {
        url += `&year=${yearFilter}`;
      }
      
      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch reports');
      const data = await response.json();
      setReports(data.items || []);
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, yearFilter]);

  // Fetch default tax rate
  const fetchDefaultTaxRate = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/tax/zones`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        const defaultZone = data.items?.find(z => z.is_default);
        setDefaultTaxRate(defaultZone);
      }
    } catch (error) {
      console.error('Error fetching default tax rate:', error);
    }
  };

  useEffect(() => {
    fetchReports();
    fetchDefaultTaxRate();
  }, [fetchReports]);

  // Generate new report
  const handleGenerateReport = async () => {
    if (!selectedPeriod) {
      toast.error('Please select a period');
      return;
    }

    try {
      setGenerating(true);
      const response = await fetch(`${API_BASE}/api/tax/ca/reports/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ period_label: selectedPeriod }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate report');
      }

      const report = await response.json();
      toast.success(`Report generated for ${selectedPeriod}`);
      setShowGenerateModal(false);
      setSelectedPeriod('');
      fetchReports();
      // Navigate to report detail
      if (onViewReport) {
        onViewReport(report.id);
      }
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error(error.message);
    } finally {
      setGenerating(false);
    }
  };

  // Delete report
  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this draft report?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/tax/ca/reports/${reportId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete report');
      }

      toast.success('Report deleted');
      fetchReports();
    } catch (error) {
      console.error('Error deleting report:', error);
      toast.error(error.message);
    }
  };

  // Calculate stats from reports
  const stats = {
    total: reports.length,
    draft: reports.filter(r => r.status === 'draft').length,
    filed: reports.filter(r => r.status === 'filed').length,
    totalSales: reports.reduce((sum, r) => sum + (r.totals?.total_sales_paid || 0), 0),
    totalCollected: reports.reduce((sum, r) => sum + (r.totals?.tax_collected_paid || 0), 0),
    totalOwed: reports.reduce((sum, r) => sum + (r.totals?.tax_owed_paid || 0), 0),
  };

  return (
    <div className="space-y-6" data-testid="sales-tax-reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">Sales Tax Reports</h1>
          <p className="text-muted-foreground text-sm mt-1">
            CA-only tax reporting for CDTFA compliance (Cash Basis)
          </p>
        </div>
        <Button onClick={() => setShowGenerateModal(true)} data-testid="generate-report-btn">
          <Plus className="h-4 w-4 mr-2" />
          Generate Report
        </Button>
      </div>

      {/* Cash Basis Info Banner */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="py-3">
          <div className="flex items-start gap-2 text-sm">
            <Banknote className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
            <div className="text-blue-800">
              <strong>Cash Basis Reporting:</strong> Only PAID invoices are included. Tax is owed when payment is received.
              {defaultTaxRate && (
                <span className="ml-2">
                  Default Rate: <strong>{defaultTaxRate.city}, {defaultTaxRate.state} – {formatPercent(defaultTaxRate.rate)}</strong>
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Total Reports</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-yellow-600" />
              <div>
                <p className="text-sm text-muted-foreground">Draft</p>
                <p className="text-2xl font-bold">{stats.draft}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-sm text-muted-foreground">Filed</p>
                <p className="text-2xl font-bold">{stats.filed}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-slate-600" />
              <div>
                <p className="text-sm text-muted-foreground">Total Sales</p>
                <p className="text-lg font-bold">{formatCurrency(stats.totalSales)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-sm text-muted-foreground">Tax Collected</p>
                <p className="text-lg font-bold">{formatCurrency(stats.totalCollected)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-orange-600" />
              <div>
                <p className="text-sm text-muted-foreground">Tax Owed</p>
                <p className="text-lg font-bold">{formatCurrency(stats.totalOwed)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-4">
            <div className="w-40">
              <Label className="text-xs text-muted-foreground">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="final">Final</SelectItem>
                  <SelectItem value="filed">Filed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-32">
              <Label className="text-xs text-muted-foreground">Year</Label>
              <Select value={yearFilter} onValueChange={setYearFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Years" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Years</SelectItem>
                  {yearOptions.map(year => (
                    <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reports Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Reports</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : reports.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No reports found</p>
              <Button
                variant="link"
                onClick={() => setShowGenerateModal(true)}
                className="mt-2"
              >
                Generate your first report
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Period</TableHead>
                  <TableHead>Basis</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Invoices</TableHead>
                  <TableHead className="text-right">
                    Total Sales
                    <HelpTooltip text={COLUMN_HELP.totalSales} />
                  </TableHead>
                  <TableHead className="text-right">
                    Shipping
                    <HelpTooltip text={COLUMN_HELP.shipping} />
                  </TableHead>
                  <TableHead className="text-right">
                    Taxable
                    <HelpTooltip text={COLUMN_HELP.taxable} />
                  </TableHead>
                  <TableHead className="text-right">
                    Non-Taxable
                    <HelpTooltip text={COLUMN_HELP.nonTaxable} />
                  </TableHead>
                  <TableHead className="text-right">
                    Tax Collected
                    <HelpTooltip text={COLUMN_HELP.taxCollected} />
                  </TableHead>
                  <TableHead className="text-right">
                    Tax Owed
                    <HelpTooltip text={COLUMN_HELP.taxOwed} />
                  </TableHead>
                  <TableHead className="text-right">
                    Over-Collected
                    <HelpTooltip text={COLUMN_HELP.overCollected} />
                  </TableHead>
                  <TableHead className="text-center">
                    Exceptions
                    <HelpTooltip text={COLUMN_HELP.exceptions} />
                  </TableHead>
                  <TableHead>Generated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reports.map((report) => (
                  <TableRow key={report.id} data-testid={`report-row-${report.id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        {report.period?.label}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {report.basis || 'cash'}
                      </Badge>
                    </TableCell>
                    <TableCell>{getStatusBadge(report.status)}</TableCell>
                    <TableCell className="text-right">{report.totals?.invoice_count || 0}</TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(report.totals?.total_sales_paid)}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {formatCurrency(report.totals?.total_shipping_paid)}
                    </TableCell>
                    <TableCell className="text-right font-medium text-blue-600">
                      {formatCurrency(report.totals?.ca_taxable_sales_paid)}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {formatCurrency(report.totals?.non_taxable_sales_paid)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(report.totals?.tax_collected_paid)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(report.totals?.tax_owed_paid)}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={report.totals?.over_collected_paid > 0 ? 'text-green-600' : report.totals?.over_collected_paid < 0 ? 'text-red-600' : ''}>
                        {formatCurrency(report.totals?.over_collected_paid)}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      {(report.exceptions?.missing_ship_state_count || 0) + 
                       (report.exceptions?.missing_tax_snapshot_count || 0) +
                       (report.exceptions?.non_ca_tax_collected_count || 0) > 0 ? (
                        <Badge variant="destructive" className="gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          {(report.exceptions?.missing_ship_state_count || 0) + 
                           (report.exceptions?.missing_tax_snapshot_count || 0) +
                           (report.exceptions?.non_ca_tax_collected_count || 0)}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-green-600">None</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {report.generated_at ? new Date(report.generated_at).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onViewReport && onViewReport(report.id)}
                          data-testid={`view-report-${report.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(`${API_BASE}/api/tax/ca/reports/${report.id}/export/summary`, '_blank')}
                          title="Download Summary CSV"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        {report.status === 'draft' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteReport(report.id)}
                            className="text-red-600 hover:text-red-700"
                            data-testid={`delete-report-${report.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {/* Aggregate Totals Row */}
                {reports.length > 0 && (
                  <TableRow className="bg-muted/50 border-t-2" data-testid="totals-row">
                    <TableCell colSpan={3} className="font-bold text-right">
                      TOTALS
                    </TableCell>
                    <TableCell className="text-right font-bold">
                      {reports.reduce((sum, r) => sum + (r.totals?.invoice_count || 0), 0)}
                    </TableCell>
                    <TableCell className="text-right font-bold">
                      {formatCurrency(reports.reduce((sum, r) => sum + (r.totals?.total_sales_paid || 0), 0))}
                    </TableCell>
                    <TableCell className="text-right font-bold text-muted-foreground">
                      {formatCurrency(reports.reduce((sum, r) => sum + (r.totals?.total_shipping_paid || 0), 0))}
                    </TableCell>
                    <TableCell className="text-right font-bold text-blue-600">
                      {formatCurrency(reports.reduce((sum, r) => sum + (r.totals?.ca_taxable_sales_paid || 0), 0))}
                    </TableCell>
                    <TableCell className="text-right font-bold text-muted-foreground">
                      {formatCurrency(reports.reduce((sum, r) => sum + (r.totals?.non_taxable_sales_paid || 0), 0))}
                    </TableCell>
                    <TableCell className="text-right font-bold">
                      {formatCurrency(reports.reduce((sum, r) => sum + (r.totals?.tax_collected_paid || 0), 0))}
                    </TableCell>
                    <TableCell className="text-right font-bold">
                      {formatCurrency(reports.reduce((sum, r) => sum + (r.totals?.tax_owed_paid || 0), 0))}
                    </TableCell>
                    <TableCell className="text-right font-bold">
                      {(() => {
                        const total = reports.reduce((sum, r) => sum + (r.totals?.over_collected_paid || 0), 0);
                        return (
                          <span className={total > 0 ? 'text-green-600' : total < 0 ? 'text-red-600' : ''}>
                            {formatCurrency(total)}
                          </span>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-center font-bold">
                      {reports.reduce((sum, r) => 
                        sum + (r.exceptions?.missing_ship_state_count || 0) + 
                        (r.exceptions?.missing_tax_snapshot_count || 0) +
                        (r.exceptions?.non_ca_tax_collected_count || 0), 0
                      )}
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Generate Report Modal */}
      <Dialog open={showGenerateModal} onOpenChange={setShowGenerateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate Sales Tax Report</DialogTitle>
            <DialogDescription>
              Select a period to generate a CA sales tax report. This will analyze all <strong>PAID</strong> invoices
              (cash basis) during the selected month and compute tax owed based on fulfillment type and ship-to addresses.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Report Period</Label>
              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger data-testid="period-select">
                  <SelectValue placeholder="Select a month..." />
                </SelectTrigger>
                <SelectContent>
                  {periodOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="rounded-lg bg-muted/50 p-3 text-sm space-y-2">
              <p className="font-medium">Cash Basis Rules:</p>
              <ul className="text-muted-foreground text-xs space-y-1 ml-4 list-disc">
                <li>Only <strong>PAID</strong> invoices are included</li>
                <li>Tax owed is based on <strong>paid_at</strong> date</li>
                <li><strong>PICKUP</strong> orders use default CA rate</li>
                <li><strong>SHIP</strong> to CA = default rate, non-CA = 0%</li>
                <li>Tax exempt customers = 0%</li>
              </ul>
              {defaultTaxRate && (
                <p className="text-muted-foreground pt-2 border-t">
                  Default Rate: <strong>{defaultTaxRate.city}, {defaultTaxRate.state} – {formatPercent(defaultTaxRate.rate)}</strong>
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleGenerateReport} 
              disabled={generating || !selectedPeriod}
              data-testid="confirm-generate-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Generate Report
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
